import React from 'react'
import type { WpsPreviewTemplateData } from '../../../utils/wpsPreviewClient'
import SignatoryMetaLines from '../shared/SignatoryMetaLines'
import WpsPreviewFieldLink from './WpsPreviewFieldLink'
import type {
  WpsFieldDescriptor,
  WpsFieldLinkContext
} from './wpsFieldLinkRegistry'
import { cssVars as wpsPreviewCssVars } from './wpsPreviewTheme'
import { wpsPreviewCssVars as buildWpsPreviewCssVars } from './wpsPreviewCssVars'

/**
 * HTML/CSS mirror of `WpsTemplate.tsx` — renders the same sections in
 * roughly the same layout so the on-screen preview matches the
 * generated PDF byte-for-byte (text contents) without round-tripping
 * through PDF generation + S3.
 *
 * Intentionally streamlined: this is the read-only canvas — edits are
 * mediated through the page's modals (Identifiers / Approvals) +
 * staging logic in `WpsPreviewPage.tsx`.
 */
/**
 * Optional click-to-navigate wrapper. When a `fieldLinkContext` is
 * supplied (the page is in editable mode), section cells whose data
 * lives on a WPS section form are wrapped in `WpsPreviewFieldLink`
 * so a click round-trips the user to the source page with focus
 * params + a `returnTo` back to this preview. When the context is
 * `null` (e.g. read-only embeds, tests), the wrapper falls through
 * to its children unchanged.
 */
export const WpsFieldLinkContextContext =
  React.createContext<WpsFieldLinkContext | null>(null)

export default function WpsPreviewCanvas({
  data,
  fieldLinkContext = null
}: {
  data: WpsPreviewTemplateData
  fieldLinkContext?: WpsFieldLinkContext | null
}) {
  return (
    <WpsFieldLinkContextContext.Provider value={fieldLinkContext}>
      <div
        className="relative isolate mx-auto w-[length:var(--wps-page-width)] max-w-full border border-[color:var(--wps-paper-edge)] p-[length:var(--wps-page-margin)]"
        data-testid="wps-preview-canvas"
        style={{
          ...buildWpsPreviewCssVars(wpsPreviewCssVars),
          fontFamily: 'var(--wps-font-family)',
          minWidth: 0
        }}
      >
        <TopBand data={data} />
        <TopMetaBlock data={data} />
        <BaseMaterialJointBevelPair data={data} />
        <ClampPassSequencePair data={data} />
        <WeldingParametersTable data={data} />
        <CleaningPostWeldBand data={data} />
        <NotesBlock data={data} />
        <DisclaimerPanel data={data} />
        <ApprovalsFooter data={data} />
        <p className="mt-3 text-right text-[10px] text-gray-400">
          Generated on: {new Date(data.generatedAt).toUTCString()}
        </p>
      </div>
    </WpsFieldLinkContextContext.Provider>
  )
}

/**
 * Convenience wrapper used by every section that wants to be
 * click-to-navigate-aware. When no context is available the
 * children render bare — preserves the existing read-only behaviour
 * for tests and embeds that don't supply a context.
 */
function MaybeFieldLink({
  descriptor,
  fieldLabel,
  className,
  children,
  as,
  colSpan
}: {
  descriptor: WpsFieldDescriptor
  fieldLabel: string
  className?: string
  children: React.ReactNode
  as?: 'div' | 'td'
  colSpan?: number
}) {
  const ctx = React.useContext(WpsFieldLinkContextContext)
  if (!ctx) {
    if (as === 'td') {
      return (
        <td className={className} colSpan={colSpan}>
          {children}
        </td>
      )
    }
    if (className) {
      return <div className={className}>{children}</div>
    }
    return <>{children}</>
  }
  return (
    <WpsPreviewFieldLink
      descriptor={descriptor}
      fieldLabel={fieldLabel}
      context={ctx}
      className={className}
      as={as}
      colSpan={colSpan}
    >
      {children}
    </WpsPreviewFieldLink>
  )
}

/**
 * Slice 01 of wps-pdf-xlsx-faithful — title band rewritten to mirror
 * XLSX rows 2-3 of the "Ridgeline WPS3" sheet. The previous brand-red
 * ribbon is replaced with a plain centred title + right-aligned
 * identifier block, both on a thin black bottom border. Font family
 * is sourced from `wpsPreviewTheme.cssVars['--wps-font-family']` so
 * the on-screen preview metrics match the generated PDF and changes
 * to the token propagate through one read (slice 01 of
 * wps-preview-pixel-parity). The `TITLE_FONT_STACK` re-export keeps
 * the existing per-section callers wired through this single token
 * read — change the token in `wpsPreviewTheme.ts`, every section
 * picks it up.
 */
const TITLE_FONT_STACK = wpsPreviewCssVars['--wps-font-family']

/** Shared 55/45 paired-band row — flex (not grid) so stacked right-column panels do not each claim min-h-full and overflow. */
const PAIRED_BAND_ROW =
  'mt-3 flex flex-row items-stretch overflow-hidden border wps-hairline border-[color:var(--wps-hairline-color)]'
const PAIRED_BAND_LEFT =
  'flex w-[55%] shrink-0 flex-col self-stretch border-r wps-hairline-r border-[color:var(--wps-hairline-color)] min-w-0'
const PAIRED_BAND_RIGHT =
  'flex w-[45%] shrink-0 flex-col self-stretch min-w-0 overflow-hidden'

function TopBand({ data }: { data: WpsPreviewTemplateData }) {
  return (
    <div
      data-testid="wps-title-band"
      className="mb-1 flex items-center gap-2 border-b-2 border-[color:var(--wps-brand-red)] pb-1"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <div className="flex h-14 w-[140px] shrink-0 items-center justify-center">
        <img
          src="/crc-logo.png"
          alt="CRC Evans"
          className="h-14 w-[140px] object-contain"
        />
      </div>
      <div className="min-w-0 flex-1 text-center">
        <h1 className="m-0 text-[length:var(--wps-font-size-title)] font-bold uppercase tracking-wide text-black">
          Welding Procedure Specification
        </h1>
      </div>
      <MaybeFieldLink
        descriptor={{ section: 'identifiers' }}
        fieldLabel="WPS identifiers"
        className="min-w-[160px] shrink-0 border border-[color:var(--wps-hairline-color)] border-l-[3px] border-l-[color:var(--wps-brand-red)] p-1 text-right text-[length:var(--wps-font-size-body)] text-black"
      >
        <p className="font-semibold">{data.identifiers.wpsNumber}</p>
        <p>{data.identifiers.sheetOf}</p>
        <p>Rev. {data.identifiers.revision}</p>
      </MaybeFieldLink>
    </div>
  )
}

/**
 * Lowercase + strip punctuation to produce a stable `focusField`
 * marker for the `project-info` descriptor in slice 04 of
 * `wps-preview-pixel-parity`. Examples:
 *   "Contractor"          -> "contractor"
 *   "CRC Weld File No."   -> "crc-weld-file-no"
 *   "Project Mgr"         -> "project-mgr"
 *
 * The destination page (`ProjectCreate`) consumes these markers
 * via its `useFieldFocusHandler` registry — adding a new marker
 * for a top-meta label is a paired change between this helper and
 * the project edit page.
 */
function slugifyMetaLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Fallback parameter slug used when a welding-parameter row arrives
 * without a stable `key` field (back-compat for reports persisted
 * before slice 05 of `wps-preview-pixel-parity`). Strips units in
 * parentheses ("Voltage (V)" → "voltage"), lowercases, and replaces
 * non-alphanumerics with hyphens — same shape as
 * `slugifyMetaLabel`. New rows arrive with `key` populated upstream
 * so this branch is only hit on legacy data.
 */
function slugifyParameterLabel(label: string): string {
  return label
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Convert the row-major top-meta grid into a column-major view —
 * one list per visible column, each cell carrying its `rowSpan`
 * weight. `null` cells are dropped because the cell above already
 * claimed their height via `rowSpan: 2` (mirrors the `G5:H6` /
 * `I5:L6` Equipment merge in the source spreadsheet). Mirrors the
 * helper of the same name on the server-side renderer.
 */
function topMetaGridToColumns(
  grid: WpsPreviewTemplateData['topMeta']
): Array<Array<{ cell: { label: string; value: string }; rowSpan: number }>> {
  const cols: Array<
    Array<{ cell: { label: string; value: string }; rowSpan: number }>
  > = [[], [], []]
  for (const row of grid) {
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (cell === null) continue
      cols[c].push({
        cell: { label: cell.label, value: cell.value },
        rowSpan: cell.rowSpan && cell.rowSpan > 1 ? cell.rowSpan : 1
      })
    }
  }
  return cols
}

/**
 * Per-row baseline height (pt) for top-meta cells — mirrors
 * `TOP_META_ROW_UNIT` (24pt) on the React-PDF surface so the on-screen
 * preview is dimensionally identical to the generated PDF.
 * Equipment (`rowSpan: 2`) is exactly 2× this tall.
 *
 * Suffix is `_PT` (not `_PX`) so the unit is unambiguous at every
 * call site — the value is applied directly as `pt` to mirror the
 * React-PDF surface, where 1pt = 1/72in.
 */
const TOP_META_ROW_UNIT_PT = 24

/**
 * XLSX-faithful 3-column top-meta block (slice 02 + user follow-up).
 * Renders column-major so each cell can claim its own height via
 * `rowSpan`. Within each cell the label and value sit in their own
 * table sub-cells with a vertical hairline between them, matching
 * how the spreadsheet draws the merged `[label][value]` pair (rows
 * 4-6 of the source spreadsheet).
 *
 * Heights are pinned with `minHeight = TOP_META_ROW_UNIT_PT × rowSpan`
 * AND `flex-basis: 0` per cell so the Equipment cell is exactly 2× as
 * tall as its peers regardless of how many lines the wrapped text in
 * each cell ends up taking. Without the `flex-basis: 0` override the
 * cells would size to content (Application 1 line vs Equipment 4
 * lines) and the 1:2 ratio would never materialise.
 */
function TopMetaBlock({ data }: { data: WpsPreviewTemplateData }) {
  const cols = topMetaGridToColumns(data.topMeta)
  return (
    <div
      data-testid="wps-top-meta-block"
      className="mt-3 flex flex-row items-stretch border wps-hairline border-[color:var(--wps-hairline-color)]"
      style={{
        fontFamily: TITLE_FONT_STACK,
        minHeight: `${TOP_META_ROW_UNIT_PT * 3}pt`
      }}
    >
      {cols.map((colCells, colIdx) => {
        const isLastCol = colIdx === cols.length - 1
        return (
          <div
            key={`xlsx-meta-col-${colIdx}`}
            data-testid={`wps-top-meta-col-${colIdx}`}
            className={`flex flex-1 flex-col ${
              isLastCol ? '' : 'border-r wps-hairline-r border-[color:var(--wps-hairline-color)]'
            }`}
          >
            {colCells.map(({ cell, rowSpan }, cellIdx) => {
              const isLastInCol = cellIdx === colCells.length - 1
              const focusField = slugifyMetaLabel(cell.label)
              return (
                <MaybeFieldLink
                  key={`xlsx-meta-cell-${colIdx}-${cellIdx}`}
                  descriptor={{ section: 'project-info', focusField }}
                  fieldLabel={cell.label || `Top meta cell ${colIdx}-${cellIdx}`}
                  className={`flex items-stretch ${
                    isLastInCol ? '' : 'border-b wps-hairline-b border-[color:var(--wps-hairline-color)]'
                  }`}
                >
                  <div
                    data-testid={`wps-top-meta-cell-${colIdx}-${cellIdx}`}
                    data-rowspan={rowSpan}
                    className="flex w-full items-stretch"
                    style={{
                      flexGrow: rowSpan,
                      flexShrink: 0,
                      flexBasis: 0,
                      minHeight: `${TOP_META_ROW_UNIT_PT * rowSpan}pt`
                    }}
                  >
                    <span className="w-[45%] border-r wps-hairline-r border-[color:var(--wps-hairline-color)] p-[4pt] text-[length:var(--wps-font-size-body)] font-bold text-black break-words">
                      {cell.label}
                    </span>
                    <span className="flex-1 p-[4pt] text-[length:var(--wps-font-size-body)] text-black break-words">
                      {cell.value}
                    </span>
                  </div>
                </MaybeFieldLink>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

/**
 * XLSX-faithful section header band — brand-red fill with white
 * Carlito Bold text. Used by every body section of the WPS PDF /
 * preview that mirrors the XLSX header rows (slice 03 onwards of
 * wps-pdf-xlsx-faithful). The legacy lighter pink ribbon is gone.
 */
function SectionTitle({
  children,
  testId
}: {
  children: string
  testId?: string
}) {
  return (
    <h2
      data-testid={testId}
      className="bg-[color:var(--wps-brand-red)] p-[4pt] text-[length:var(--wps-font-size-section-header)] font-bold text-white"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      {children}
    </h2>
  )
}

/**
 * XLSX-faithful key-value row — 0.5pt black borders, Carlito body,
 * label on the left in bold, value flexes on the right. Cell heights
 * grow to accommodate wrapped long values.
 */
function KvRow({
  label,
  value,
  fillHeight = false
}: {
  label: string
  value: string
  fillHeight?: boolean
}) {
  return (
    <div
      className={`flex items-stretch border-b wps-hairline-b border-[color:var(--wps-hairline-color)] last:border-b-0 ${
        fillHeight ? 'min-h-0 flex-1' : ''
      }`}
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <span className="flex w-2/5 shrink-0 self-stretch items-center border-r wps-hairline-r border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] font-bold text-black">
        {label}
      </span>
      <span className="flex flex-1 self-stretch items-center px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] text-black break-words">
        {value}
      </span>
    </div>
  )
}

/**
 * XLSX-faithful 4-cell row used for the Base Material diameter and
 * wall-thickness rows (source spreadsheet rows 9-10). Renders as
 * `[label]  [min]   to   [max]` — three discrete value cells so the
 * preview matches the spreadsheet's cell-by-cell layout.
 */
function KvRangeRow({
  label,
  min,
  max
}: {
  label: string
  min: string
  max: string
}) {
  return (
    <div
      className="flex items-stretch border-b wps-hairline-b border-[color:var(--wps-hairline-color)] last:border-b-0"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <span className="flex w-2/5 shrink-0 self-stretch items-center border-r wps-hairline-r border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] font-bold text-black">
        {label}
      </span>
      <span className="flex w-[22%] shrink-0 self-stretch items-center justify-center border-r wps-hairline-r border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] text-black">
        {min || '—'}
      </span>
      <span className="flex w-[16%] shrink-0 self-stretch items-center justify-center border-r wps-hairline-r border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] text-black">
        to
      </span>
      <span className="flex w-[22%] shrink-0 self-stretch items-center justify-center px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-body)] text-black">
        {max || '—'}
      </span>
    </div>
  )
}

/**
 * XLSX-faithful Base Material + Joint/Bevel paired band (slice 03 of
 * wps-pdf-xlsx-faithful). Mirrors XLSX rows 7-16 of the source
 * spreadsheet: left half stacks Base Material Information (7 rows)
 * above Joint/Bevel Information (3 rows), right half stacks the
 * schematic image above an 8-row dimension table.
 */
function BaseMaterialJointBevelPair({
  data
}: {
  data: WpsPreviewTemplateData
}) {
  const bm = data.baseMaterial
  const jointEntries =
    Array.isArray(data.jointBevels) && data.jointBevels.length > 0
      ? data.jointBevels
      : [data.jointBevel]

  return (
    <>
      {jointEntries.map((jb, idx) => (
        <div
          key={`${jb.weldId ?? 'jb'}-${idx}`}
          data-testid={idx === 0 ? 'wps-base-pair-band' : `wps-base-pair-band-${idx}`}
          className={PAIRED_BAND_ROW}
          style={{ fontFamily: TITLE_FONT_STACK }}
        >
          <div className={PAIRED_BAND_LEFT}>
            {idx === 0 ? (
              <MaybeFieldLink
                descriptor={{ section: 'base-material' }}
                fieldLabel="Base Material Information"
              >
                <SectionTitle testId="wps-section-header-base-material">
                  Base Material Information
                </SectionTitle>
                <div data-testid="wps-base-material-left">
                  <KvRow label="Material Specification:" value={bm.materialSpec || '—'} />
                  <KvRow label="Type or Grade:" value={bm.typeOrGrade || '—'} />
                  <KvRangeRow
                    label="Nominal Diameter (in):"
                    min={bm.nominalDiameterMin || ''}
                    max={bm.nominalDiameterMax || ''}
                  />
                  <KvRangeRow
                    label="Wall Thickness (in):"
                    min={bm.wallThicknessMin || ''}
                    max={bm.wallThicknessMax || ''}
                  />
                  <KvRow label="Slab Source:" value={bm.slabSource || '—'} />
                  <KvRow label="Plate Source:" value={bm.plateSource || '—'} />
                  <KvRow
                    label="Pipe Manufacturer:"
                    value={bm.pipeManufacturer || '—'}
                  />
                </div>
              </MaybeFieldLink>
            ) : null}
            <MaybeFieldLink
              descriptor={{ section: 'joint-bevel' }}
              fieldLabel="Joint/Bevel Information"
            >
              <SectionTitle testId={idx === 0 ? 'wps-section-header-joint-bevel' : `wps-section-header-joint-bevel-${idx}`}>
                {`Joint/Bevel Information${jb.weldId ? ` · ${jb.weldId}` : ''}`}
              </SectionTitle>
              <div data-testid={idx === 0 ? 'wps-joint-bevel-left' : `wps-joint-bevel-left-${idx}`}>
                <KvRow label="Joint Design/Type:" value={jb.jointType || '—'} />
                <KvRow
                  label="Backing Requirements:"
                  value={jb.backingRequirements || '—'}
                />
                <KvRow label="Backing Material:" value={jb.backingMaterial || '—'} />
              </div>
            </MaybeFieldLink>
          </div>
          <MaybeFieldLink
            descriptor={{ section: 'joint-bevel' }}
            fieldLabel="Bevel Schematic"
            className={PAIRED_BAND_RIGHT}
          >
            <div
              data-testid={idx === 0 ? 'wps-bevel-schematic' : `wps-bevel-schematic-${idx}`}
              className="flex min-h-[130pt] w-full items-center justify-center overflow-hidden border-b wps-hairline-b border-[color:var(--wps-hairline-color)] p-2"
            >
              {jb.imageUrl ? (
                <img
                  src={jb.imageUrl}
                  alt="Schematic of Joint Design"
                  className="block max-h-[160pt] max-w-full object-contain"
                />
              ) : (
                <span className="text-[length:var(--wps-font-size-body)] italic text-gray-500">
                  Schematic of Joint Design
                </span>
              )}
            </div>
            <div data-testid={idx === 0 ? 'wps-bevel-dimension-table' : `wps-bevel-dimension-table-${idx}`}>
              {jb.dimensions.map((d) => (
                <div
                  key={d.key}
                  data-testid={`wps-bevel-dimension-row-${idx}-${d.key}`}
                  className="flex items-stretch border-b wps-hairline-b border-[color:var(--wps-hairline-color)] last:border-b-0"
                >
                  <span className="flex w-1/2 shrink-0 self-stretch items-center border-r wps-hairline-r border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-caption)] font-bold text-black">
                    {d.label}
                  </span>
                  <span className="flex flex-1 self-stretch items-center px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-caption)] text-black">
                    {d.value}
                  </span>
                </div>
              ))}
            </div>
          </MaybeFieldLink>
        </div>
      ))}
    </>
  )
}

/**
 * XLSX-faithful Clamp / Preheat / Welder / WeldTimes paired band
 * (slice 04 of wps-pdf-xlsx-faithful). Mirrors XLSX rows 17-30 of
 * the source spreadsheet — left half stacks four brand-red section
 * headers with their KV rows, right half hosts the Pass Sequence
 * panel with its diagram and notes-pdf string.
 */
function ClampPassSequencePair({ data }: { data: WpsPreviewTemplateData }) {
  const cp = data.clampPreheat
  const passEntries =
    Array.isArray(data.weldPassSequences) && data.weldPassSequences.length > 0
      ? data.weldPassSequences
      : [data.weldPassSequence]
  return (
    <div
      data-testid="wps-clamp-pair-band"
      className={PAIRED_BAND_ROW}
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <div data-testid="wps-clamp-pair-left" className={PAIRED_BAND_LEFT}>
        <MaybeFieldLink
          descriptor={{ section: 'clamp' }}
          fieldLabel="Clamp Information"
        >
          <SectionTitle testId="wps-section-header-clamp">
            Clamp Information
          </SectionTitle>
          <KvRow label="Line-up Clamp:" value={cp.clamp.lineUpClamp || '—'} />
          <KvRow label="Removal:" value={cp.clamp.removal || '—'} />
        </MaybeFieldLink>

        <MaybeFieldLink
          descriptor={{ section: 'preheat-interpass' }}
          fieldLabel="Preheat/ Interpass Information"
        >
          <SectionTitle testId="wps-section-header-preheat">
            Preheat/ Interpass Information
          </SectionTitle>
          <KvRow
            label="Preheat Temperature:"
            value={`Min: ${cp.preheat.preheatMin || '—'}`}
          />
          <KvRow
            label="Interpass Temperature:"
            value={`Min: ${cp.preheat.interpassMin || '—'}    Max.: ${cp.preheat.interpassMax || '—'}`}
          />
          <KvRow label="Heating Method:" value={cp.preheat.heatingMethod || '—'} />
          <KvRow label="Control Method:" value={cp.preheat.controlMethod || '—'} />
          {cp.preheat.notes.length > 0 ? (
            <div
              data-testid="wps-preheat-footnotes"
              className="border-b wps-hairline-b border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-caption)] text-black"
            >
              <ul className="list-disc pl-4">
                {cp.preheat.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </MaybeFieldLink>

        <MaybeFieldLink
          descriptor={{ section: 'welder-counts' }}
          fieldLabel="Operator/Welder Information"
        >
          <SectionTitle testId="wps-section-header-welders">
            Operator/Welder Information
          </SectionTitle>
          <KvRow
            label="Min. # of welders root pass:"
            value={cp.welders.minRootCount || '—'}
          />
          <KvRow
            label="Min. # of welders remaining:"
            value={cp.welders.minRemainingCount || '—'}
          />
        </MaybeFieldLink>

        <MaybeFieldLink
          descriptor={{ section: 'weld-times' }}
          fieldLabel="Weld Times"
        >
          <SectionTitle testId="wps-section-header-weldtimes">
            Weld Times
          </SectionTitle>
          <KvRow
            label="Max Time between Root & Hot Pass:"
            value={cp.weldTimes.maxRootToHot || '—'}
          />
          <KvRow
            label="Max Time between Hot Pass & Fill 1:"
            value={cp.weldTimes.maxHotToFill || '—'}
          />
        </MaybeFieldLink>
      </div>
      <div className={PAIRED_BAND_RIGHT}>
        {passEntries.map((ws, idx) => (
          <MaybeFieldLink
            key={`${ws.weldId ?? 'ws'}-${idx}`}
            descriptor={{ section: 'pass-sequence' }}
            fieldLabel="Pass Sequence"
            className="flex w-full flex-col"
          >
            <div
              data-testid={idx === 0 ? 'wps-pass-sequence-panel' : `wps-pass-sequence-panel-${idx}`}
            >
              <SectionTitle
                testId={idx === 0 ? 'wps-section-header-pass-sequence' : `wps-section-header-pass-sequence-${idx}`}
              >
                {`Pass Sequence${ws.weldId ? ` · ${ws.weldId}` : ''}`}
              </SectionTitle>
              <div
                data-testid={idx === 0 ? 'wps-pass-sequence-diagram' : `wps-pass-sequence-diagram-${idx}`}
                className="flex min-h-[160pt] w-full items-center justify-center overflow-hidden border-b wps-hairline-b border-[color:var(--wps-hairline-color)] p-2"
              >
                {ws.imageUrl ? (
                  <img
                    src={ws.imageUrl}
                    alt="Pass Sequence Diagram"
                    className="block max-h-[220pt] max-w-full object-contain"
                  />
                ) : (
                  <span className="text-[length:var(--wps-font-size-body)] italic text-gray-500">
                    Pass Sequence
                  </span>
                )}
              </div>
              {idx === 0 && cp.weldTimes.passNotesPdf ? (
                <p className="px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-caption)] italic text-black">
                  {cp.weldTimes.passNotesPdf}
                </p>
              ) : null}
            </div>
          </MaybeFieldLink>
        ))}
      </div>
    </div>
  )
}

/**
 * XLSX-faithful Welding Parameters table (slice 05 of
 * wps-pdf-xlsx-faithful). The "true split header" is implemented by:
 *  - A single 5-column main header row spanning the table.
 *  - Non-range rows render one cell per main column (`colSpan={2}` so
 *    the cell width matches a Min+Max pair).
 *  - The Min/Max sub-header row appears exactly once, immediately
 *    above the FIRST range row (`firstRangeIdx`). It does NOT appear
 *    in the table header. Once below it, range rows render 10
 *    sub-cells, with cell widths aligned to the non-range cells
 *    above and below.
 */
function WeldingParametersTable({ data }: { data: WpsPreviewTemplateData }) {
  const { columns, rows } = data.weldingParameters
  const firstRangeIdx = rows.findIndex((r) => r.isRange)
  return (
    <div
      className="mt-3 border wps-hairline border-[color:var(--wps-hairline-color)]"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <h2
        data-testid="wps-section-header-welding-parameters"
        className="bg-[color:var(--wps-brand-red)] p-[4pt] text-[length:var(--wps-font-size-section-header)] font-bold text-white"
      >
        Welding Parameters (4)
      </h2>
      <table className="w-full table-fixed border-collapse text-[length:var(--wps-font-size-caption)]">
        <thead>
          <tr
            data-testid="wps-welding-params-main-header"
            className="bg-[color:var(--wps-brand-red)] text-white"
          >
            <th
              className="w-[20%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-left"
              colSpan={1}
            >
              Variable
            </th>
            {columns.map((c) => (
              <th
                key={c.id}
                colSpan={2}
                className="w-[16%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)]"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const isFirstRange = ri === firstRangeIdx
            const subHeader =
              isFirstRange ? (
                <tr
                  key={`subheader-${ri}`}
                  data-testid="wps-welding-params-minmax-subheader"
                  className="bg-[color:var(--wps-brand-red-tint)] text-black"
                >
                  <th className="w-[20%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)]"> </th>
                  {columns.map((c) =>
                    ['Min', 'Max'].map((sub) => (
                      <th
                        key={`${c.id}-${sub}`}
                        className="w-[8%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-micro)] font-bold"
                      >
                        {sub}
                      </th>
                    ))
                  )}
                </tr>
              ) : null
            const columnKey = row.key || slugifyParameterLabel(row.label)
            const tr = (
              <tr
                key={row.label}
                data-testid={`wps-welding-params-row-${row.label}`}
              >
                <td className="w-[20%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-[length:var(--wps-font-size-caption)]">
                  {row.label}
                  {row.unit ? (
                    <span className="ml-1 text-black">{row.unit}</span>
                  ) : null}
                </td>
                {row.values.map((v, ci) => {
                  const passLabel = columns[ci]?.label ?? ''
                  const cellLabel = `${row.label} — ${passLabel}`
                  const wrapDescriptor = {
                    section: 'welding-parameter-cell' as const,
                    passLabel,
                    columnKey
                  }
                  if (row.isRange && typeof v === 'object') {
                    return (
                      <React.Fragment key={ci}>
                        <MaybeFieldLink
                          descriptor={wrapDescriptor}
                          fieldLabel={`${cellLabel} (Min)`}
                          as="td"
                          className="w-[8%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-center"
                        >
                          <span
                            data-testid={`wps-welding-params-cell-${columnKey}-${passLabel}-min`}
                          >
                            {v.min}
                          </span>
                        </MaybeFieldLink>
                        <MaybeFieldLink
                          descriptor={wrapDescriptor}
                          fieldLabel={`${cellLabel} (Max)`}
                          as="td"
                          className="w-[8%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-center"
                        >
                          <span
                            data-testid={`wps-welding-params-cell-${columnKey}-${passLabel}-max`}
                          >
                            {v.max}
                          </span>
                        </MaybeFieldLink>
                      </React.Fragment>
                    )
                  }
                  return (
                    <MaybeFieldLink
                      key={ci}
                      descriptor={wrapDescriptor}
                      fieldLabel={cellLabel}
                      as="td"
                      colSpan={2}
                      className="w-[16%] border wps-hairline border-[color:var(--wps-hairline-color)] px-[length:var(--wps-cell-pad-x)] py-[length:var(--wps-cell-pad-y)] text-center"
                    >
                      <span
                        data-testid={`wps-welding-params-cell-${columnKey}-${passLabel}`}
                      >
                        {typeof v === 'string' ? v : ''}
                      </span>
                    </MaybeFieldLink>
                  )
                })}
              </tr>
            )
            return subHeader ? (
              <React.Fragment key={`row-${ri}`}>
                {subHeader}
                {tr}
              </React.Fragment>
            ) : (
              tr
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * XLSX-faithful Cleaning / Post-Weld band (slice 06 of
 * wps-pdf-xlsx-faithful). Mirrors XLSX rows 59-63 of the source
 * spreadsheet: brand-red "Cleaning / Grinding" header band with a
 * full-width paragraph cell, followed by a brand-red "Post Weld
 * Heating / Cooling" header band with a two-column row (Post
 * Heating + PWHT on the left, Controlled Cooling Method on the
 * right).
 */
function CleaningPostWeldBand({ data }: { data: WpsPreviewTemplateData }) {
  const cw = data.cleaningPostWeld
  return (
    <div
      className="mt-3 border wps-hairline border-[color:var(--wps-hairline-color)]"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <MaybeFieldLink
        descriptor={{ section: 'cleaning-grinding' }}
        fieldLabel="Cleaning/Grinding"
      >
        <SectionTitle testId="wps-section-header-cleaning">
          Cleaning/Grinding
        </SectionTitle>
        <p
          data-testid="wps-cleaning-paragraph"
          className="border-b wps-hairline-b border-[color:var(--wps-hairline-color)] p-[4pt] text-[length:var(--wps-font-size-body)] text-black"
          style={{ textAlign: 'justify' }}
        >
          {cw.cleaningGrindingNotes || '—'}
        </p>
      </MaybeFieldLink>
      <MaybeFieldLink
        descriptor={{ section: 'post-weld-heating' }}
        fieldLabel="Post Weld Heating/Cooling"
      >
        <SectionTitle testId="wps-section-header-postweld">
          Post Weld Heating/Cooling
        </SectionTitle>
        <div data-testid="wps-postweld-pair" className="flex flex-row items-stretch">
          <div
            data-testid="wps-postweld-left"
            className="flex w-[55%] shrink-0 flex-col self-stretch border-r wps-hairline-r border-[color:var(--wps-hairline-color)] min-w-0"
          >
            <KvRow label="Post Heating:" value={cw.postHeating || '—'} />
            <KvRow label="PWHT:" value={cw.pwht || '—'} />
          </div>
          <div
            data-testid="wps-postweld-right"
            className="flex w-[45%] shrink-0 flex-col self-stretch min-w-0"
          >
            <KvRow
              label="Controlled Cooling Method:"
              value={cw.controlledCoolingMethod || '—'}
              fillHeight
            />
          </div>
        </div>
      </MaybeFieldLink>
    </div>
  )
}

/**
 * XLSX-faithful Notes block (slice 06 of wps-pdf-xlsx-faithful).
 * Mirrors XLSX rows 64-70 — brand-red "Notes:" header band followed
 * by a numbered footnote list in Carlito 9pt. Numbering uses the
 * DTO `id`, which other panels also reference inline (e.g. the
 * preheat footnotes resolve to these markers).
 */
function NotesBlock({ data }: { data: WpsPreviewTemplateData }) {
  if (data.notes.length === 0) return null
  return (
    <div
      className="mt-3 border wps-hairline border-[color:var(--wps-hairline-color)]"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <SectionTitle testId="wps-section-header-notes">Notes:</SectionTitle>
      <MaybeFieldLink
        descriptor={{ section: 'notes' }}
        fieldLabel="Edit WPS notes"
      >
        <ol
          data-testid="wps-notes-list"
          className="px-3 py-2 text-[length:var(--wps-font-size-caption)] text-black"
        >
          {data.notes.map((n) => (
            <li
              key={n.id}
              data-testid={`wps-notes-item-${n.id}`}
              className="mb-1"
            >
              {n.id}. {n.body}
            </li>
          ))}
        </ol>
      </MaybeFieldLink>
    </div>
  )
}

/**
 * XLSX-faithful Approvals footer (slice 07 of wps-pdf-xlsx-faithful).
 * Mirrors XLSX rows 71-79 — brand-red "Approvals" header band, then
 * three side-by-side columns each with a company-name row, a large
 * wet-ink signature box, and Signature/Name/Title/Date printed
 * lines under the box.
 */
function ApprovalsFooter({ data }: { data: WpsPreviewTemplateData }) {
  return (
    <div
      data-testid="wps-approvals-block"
      className="mt-3 border wps-hairline border-[color:var(--wps-hairline-color)]"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      <SectionTitle testId="wps-section-header-approvals">Approvals</SectionTitle>
      <div className="flex flex-row">
        {data.approvals.map((col, idx) => (
          <MaybeFieldLink
            key={col.id}
            descriptor={{ section: 'approvals' }}
            fieldLabel={`Edit ${col.label} approval`}
            className={`flex w-1/3 flex-col p-[4pt] ${idx < data.approvals.length - 1 ? 'border-r wps-hairline-r border-[color:var(--wps-hairline-color)]' : ''}`}
          >
            <div data-testid={`wps-approval-col-${col.id}`} className="flex flex-col">
              <p className="text-center text-[length:var(--wps-font-size-body)] font-bold text-black">
                {col.label}
              </p>
              <div
                data-testid={`wps-approval-signature-box-${col.id}`}
                className="mt-1 flex min-h-[60pt] items-center justify-center border wps-hairline border-[color:var(--wps-hairline-color)] bg-white"
              >
                {col.signatureImage ? (
                  <img
                    src={col.signatureImage}
                    alt={`${col.label} signature`}
                    className="max-h-[56pt] max-w-full object-contain"
                  />
                ) : null}
              </div>
              <div className="mt-1">
                <SignatoryMetaLines
                  name={col.name}
                  title={col.title}
                  date={col.date}
                />
                {col.stampImage ? (
                  <img
                    src={col.stampImage}
                    alt={`${col.label} stamp`}
                    className="mt-1 max-h-[32pt] max-w-full object-contain"
                  />
                ) : null}
              </div>
            </div>
          </MaybeFieldLink>
        ))}
      </div>
    </div>
  )
}

/**
 * XLSX-faithful disclaimer band (slice 07 of wps-pdf-xlsx-faithful).
 * Mirrors XLSX row 80 — single full-width centred italic cell with
 * the CRC Evans proprietary disclaimer text.
 */
function DisclaimerPanel({ data }: { data: WpsPreviewTemplateData }) {
  return (
    <div
      data-testid="wps-disclaimer-band"
      className="mt-3 border wps-hairline border-[color:var(--wps-hairline-color)] bg-white p-[4pt] text-center italic text-[length:var(--wps-font-size-micro)] text-black"
      style={{ fontFamily: TITLE_FONT_STACK }}
    >
      {data.disclaimer}
    </div>
  )
}
